// Hook: load + mutate responsibility tags for a wedding. Defaults are
// returned by the backend alongside customs in the same response so the
// dropdowns just merge them. We re-fetch on demand after mutations.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useResponsibilityTags(weddingId) {
  const [defaults, setDefaults] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!weddingId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/responsibility-tags/${weddingId}`,
        { withCredentials: true },
      );
      setDefaults(data?.defaults || []);
      setCustom(data?.custom || []);
    } catch (err) {
      console.error('Error loading responsibility tags:', err);
    } finally {
      setLoading(false);
    }
  }, [weddingId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createTag = useCallback(async ({ name, kind = 'custom' }) => {
    const { data } = await axios.post(
      `${API}/responsibility-tags`,
      { wedding_id: weddingId, name, kind },
      { withCredentials: true },
    );
    setCustom((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, [weddingId]);

  const updateTag = useCallback(async (tagId, payload) => {
    const { data } = await axios.put(
      `${API}/responsibility-tags/${tagId}`,
      payload,
      { withCredentials: true },
    );
    setCustom((prev) => prev.map((t) => (t.id === tagId ? data : t)).sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, []);

  const deleteTag = useCallback(async (tagId) => {
    await axios.delete(`${API}/responsibility-tags/${tagId}`, { withCredentials: true });
    setCustom((prev) => prev.filter((t) => t.id !== tagId));
  }, []);

  // Combined view consumers usually want — defaults first, then customs.
  const allTags = [...defaults, ...custom];

  return { defaults, custom, allTags, loading, refresh, createTag, updateTag, deleteTag };
}
